import {
  LABEL_PREFIX,
  LATEST_VALUE,
  VERSION_PREFIX,
  type PinnedVersionSelection,
} from "../pinned-version-select";

import type {
  PinnedVersionDropdownSections,
  PinnedVersionDropdownRow,
  PinnedVersionSelectorLabel,
  PinnedVersionSelectorVersion,
} from "./types";

export const buildPinnedVersionDropdownSections = (
  versions: PinnedVersionSelectorVersion[],
  labels: PinnedVersionSelectorLabel[],
): PinnedVersionDropdownSections => {
  const latestVersionNumber = versions.length > 0 ? Math.max(...versions.map(v => v.number)) : 0;

  const latestRow: PinnedVersionDropdownRow = {
    kind: "latest",
    versionNumber: latestVersionNumber,
    selectValue: LATEST_VALUE,
  };

  const labelRows: PinnedVersionDropdownRow[] = labels
    .map(label => ({
      kind: "label" as const,
      labelId: label.id,
      labelName: label.name,
      versionNumber: label.versionNumber,
      selectValue: `${LABEL_PREFIX}${label.name}`,
    }))
    .sort((a, b) => {
      if (b.versionNumber !== a.versionNumber) {
        return b.versionNumber - a.versionNumber;
      }
      return a.labelName.localeCompare(b.labelName);
    });

  const versionRows: PinnedVersionDropdownRow[] = versions
    .map(version => ({
      kind: "version" as const,
      versionNumber: version.number,
      selectValue: `${VERSION_PREFIX}${version.number}`,
    }))
    .sort((a, b) => b.versionNumber - a.versionNumber);

  return { latestRow, labelRows, versionRows };
};

export const getPinnedVersionTriggerText = (
  selection: PinnedVersionSelection,
  versions: PinnedVersionSelectorVersion[],
  labels: PinnedVersionSelectorLabel[],
): string => {
  const latestVersionNumber = versions.length > 0 ? Math.max(...versions.map(v => v.number)) : 0;

  switch (selection.type) {
    case "latest":
      return latestVersionNumber > 0 ? `Latest (Version ${latestVersionNumber})` : "Latest";
    case "label": {
      const label = labels.find(item => item.name === selection.labelName);
      if (label) {
        return `${selection.labelName} (Version ${label.versionNumber})`;
      }
      return selection.labelName;
    }
    case "version":
      return `Version ${selection.versionNumber}`;
    default:
      return "Latest";
  }
};
